import { atom } from 'jotai'

import {
  SORT_KEYS,
  type SortDirection,
  type SortKey
} from '@/features/vehicle-dispatch-boards/formatters'

/**
 * 配車板で現在選択中の DispatchBoardSortPreset.id。
 *
 * UserActiveDispatchBoardPreset（ユーザー×拠点単位）で DB 永続化される（Issue #4263）。
 * 初期値は画面マウント時 / 拠点切替時に GET した結果で上書きされる（use-active-dispatch-board-preset.ts）。
 * null = 未選択（マスタ Active + 当日 SPOT 末尾の初期表示）。
 */
export const activeDispatchBoardPresetIdAtom = atom<number | null>(null)

/**
 * 配車板の D&D 編集中ドラフト。planSlotId / truckId 軸の number[] で保持する。
 *
 * - null = 未編集（committed = activePreset.sortedIds を decode した順 or マスタ Active 順）
 * - 非 null = D&D で並び替えた結果、保存ボタン押下までは FE state のみ
 *
 * SortItems (tagged objects) への変換は保存時のみ実行（features/.../sort-order-codec.ts の
 * `encodeRows` で FormattedDispatchRow[] → SortItems を作る）。既存 useTruckSortOrder の
 * sortableIds と同じ軸で扱うため number[] にしている。
 *
 * 設計書 §3 sortedIds の型 / §7.2 シーケンス図「draftRows = committedRows（完全置換）、isDirty=false」と整合。
 */
export const dispatchBoardDraftOrderedIdsAtom = atom<number[] | null>(null)

/**
 * 新規配車板プリセット保存モーダル (SaveAsDispatchBoardPresetModal) の開閉状態。
 *
 * トリガー側 (DispatchBoardPresetSelectBar の「新規」メニュー / DirtyEditBanner の
 * 「名前を付けて保存」) とモーダル本体 (features 配下) が別 component に分かれている
 * ため atom 経由で接続する。
 */
export const saveAsDispatchBoardPresetOpenAtom = atom<boolean>(false)

/**
 * 配車板プリセット管理モーダル (PresetManageModal) の開閉状態。
 *
 * トリガー側 (features 配下の DispatchBoardPresetSelectBar の「管理」メニュー) と
 * モーダル本体 (routes 配下の PresetManageModal) が別レイヤに分かれているため
 * atom 経由で接続する (features → routes の import 禁忌の回避)。
 */
export const dispatchBoardPresetManageOpenAtom = atom<boolean>(false)

/**
 * 配車板一括並び替え (#3162) の選択中ソートキー。
 *
 * 設計書 §3.2: K-1〜K-7。初期値 K-1 (車両名称)。
 * リロードで初期値に戻る、日付切替やページ遷移を跨いでも state 維持。
 */
export const dispatchBoardSortKeyAtom = atom<SortKey>(SORT_KEYS.K1_NAME)

/**
 * 配車板一括並び替え (#3162) の選択中ソート方向。
 *
 * 設計書 §4.3:
 *   - 'asc' / 'desc': 昇順/降順ボタン押下中
 *   - 'custom': ソートキー変更直後 / D&D で手動編集中 / リセット直後 / Preset 切替直後 (resetDraftKey effect)
 * 描画側: 'asc' / 'desc' のときボタンを type="primary" で強調する。
 */
export const dispatchBoardSortDirectionAtom = atom<SortDirection>('custom')

/**
 * 配車板で現在ユーザーが見ている並び順の truck.id 列（partner 跨ぎフラット）。
 *
 * VehicleDispatchBoards.tsx で画面の表示順（draft 反映 + Preset 反映 + マスタ Active）の
 * 各行の truck.id を並べたものを `useEffect` 経由で同期書き込みし、配送依頼書 Excel 出力時に
 * `useVehicleDispatchDownloader` から読み取って BE に渡す。
 *
 * 配送依頼書に出力される order は必ず Dispatch（矢羽根）を持ち、Dispatch は REGULAR / SPOT を
 * 問わず truck を持つため、planSlotId / 枠種別の区別なく truck.id 一本で画面順を表現できる。
 *
 * Issue #3164 / VOC_PJ-643（「ユーザーがあくまで自分がみているものが出力される」
 * = 配車板の現在表示順を export にそのまま反映）の core mechanism。空配列 = 配車板
 * 未表示 / 配車板コンポーネント unmount 中 → BE 側は vol1 10 段階フォールバック。
 */
export const currentDispatchBoardSortedTruckIdsAtom = atom<number[]>([])
